import { type ReactNode, useMemo, useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { cn } from 'cn';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon, XIcon } from 'lucide-react';

import { InputGroup, InputGroupAddon, InputGroupInput } from './ui/input-group';

// Local override for the shadcn combobox focus state.
export const APP_FOCUS_RING_OVERRIDE = cn(
  'has-[[data-slot=input-group-control]:focus-visible]:ring-0',
  'has-[[data-slot=input-group-control]:focus-visible]:border-info-300',
  'has-[[data-slot=input-group-control]:focus-visible]:outline',
  'has-[[data-slot=input-group-control]:focus-visible]:outline-2',
  'has-[[data-slot=input-group-control]:focus-visible]:outline-primary-500',
);

export interface TreeMultiDropDownOption {
  id: string;
  label: string;
  value?: string;
  isDisabled?: boolean;
  children?: TreeMultiDropDownOption[];
}

export interface TreeMultiDropDownProps {
  className?: string;
  contentClassName?: string;
  dataTestId?: string;
  isDisabled?: boolean;
  isSearchable?: boolean;
  name?: string;
  onChange: (value: string[] | undefined, name?: string) => void;
  options: TreeMultiDropDownOption[];
  placeholder?: string;
  value?: string[];
}

const isGroup = (node: TreeMultiDropDownOption): boolean => !!node.children?.length;

const nodeMatchesSearch = (node: TreeMultiDropDownOption, term: string): boolean => {
  if (node.label.toLowerCase().includes(term)) return true;
  return node.children?.some((child) => nodeMatchesSearch(child, term)) ?? false;
};

const collectMatchingGroupIds = (nodes: TreeMultiDropDownOption[], term: string, acc: string[] = []): string[] => {
  nodes.forEach((node) => {
    if (isGroup(node) && nodeMatchesSearch(node, term)) {
      acc.push(node.id);
      collectMatchingGroupIds(node.children ?? [], term, acc);
    }
  });
  return acc;
};

const findLabel = (nodes: TreeMultiDropDownOption[], target: string): string | undefined => {
  for (const node of nodes) {
    if (!isGroup(node)) {
      if (node.value === target) return node.label;
    } else if (node.children) {
      const found = findLabel(node.children, target);
      if (found !== undefined) return found;
    }
  }
  return undefined;
};

export function TreeMultiDropDown({
  className,
  contentClassName,
  dataTestId,
  isDisabled,
  isSearchable = true,
  name,
  onChange,
  options,
  placeholder = 'Select',
  value = [],
}: TreeMultiDropDownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const selected = useMemo(() => new Set(value), [value]);
  const term = search.trim().toLowerCase();

  const emitChange = (next: Set<string>) => {
    const nextValues = Array.from(next);
    onChange(nextValues.length ? nextValues : undefined, name);
  };

  const toggleLeaf = (leafValue: string) => {
    const next = new Set(selected);
    if (next.has(leafValue)) next.delete(leafValue);
    else next.add(leafValue);
    emitChange(next);
  };

  const removeValue = (leafValue: string) => {
    const next = new Set(selected);
    next.delete(leafValue);
    emitChange(next);
  };

  const selectedItems = useMemo(
    () =>
      value.flatMap((leafValue) =>
        leafValue.split(',').map((v) => ({ value: v, label: findLabel(options, v) ?? String(v) })),
      ),
    [value, options],
  );

  const renderNodes = (nodes: TreeMultiDropDownOption[], depth: number): ReactNode =>
    nodes.map((node) => {
      if (term && !nodeMatchesSearch(node, term)) return null;

      if (!isGroup(node)) {
        const isSelected = node.value !== undefined && selected.has(node.value);
        return (
          <button
            key={node.id}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={node.isDisabled || isDisabled}
            data-testid={dataTestId ? `${dataTestId}:option-${node.id}` : undefined}
            className="hover:bg-accent flex w-full items-center gap-2 rounded-sm py-1.5 pr-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
            onClick={() => node.value !== undefined && toggleLeaf(node.value)}
          >
            {/* <TreeCheckbox state={isSelected ? 'checked' : 'unchecked'} /> */}
            <span className="min-w-0 truncate">{node.label}</span>
          </button>
        );
      }

      return (
        <AccordionItem key={node.id} value={node.id} className="border-b-0">
          <div className="flex items-center gap-2 pr-2" style={{ paddingLeft: `${depth * 16}px` }}>
            <AccordionTrigger
              data-testid={dataTestId ? `${dataTestId}:group-${node.id}` : undefined}
              className="w-full flex-1 gap-2 bg-gray-50 py-2 pl-1 text-sm font-medium hover:no-underline"
            >
              {node.label}
            </AccordionTrigger>
          </div>
          <AccordionContent className="pb-0">{renderNodes(node.children ?? [], depth + 1)}</AccordionContent>
        </AccordionItem>
      );
    });

  const matchingGroupIds = term ? collectMatchingGroupIds(options, term) : [];
  const accordionValue = term ? Array.from(new Set([...expandedIds, ...matchingGroupIds])) : expandedIds;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={isDisabled}>
        <Button
          type="button"
          variant="outline"
          disabled={isDisabled}
          data-testid={dataTestId ? `${dataTestId}:trigger` : undefined}
          className={cn(
            'hover:bg-background border-info-300 h-auto min-h-10 w-full justify-between px-3 py-2 font-normal shadow-none',
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedItems.length === 0 && <span className="text-muted-foreground font-normal">{placeholder}</span>}
            {selectedItems.map((item) => (
              <Badge
                key={String(item.value)}
                variant="secondary"
                className="bg-info-200 -my-1 gap-1 rounded-md border-0 px-2 py-1 text-sm font-normal"
              >
                <span className="max-w-40 truncate">{item.label}</span>
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove ${item.label}`}
                  data-testid={dataTestId ? `${dataTestId}:remove-item-${item.label}` : undefined}
                  className="hover:bg-info-400 flex size-5 items-center justify-center rounded-full"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeValue(item.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.stopPropagation();
                      removeValue(item.value);
                    }
                  }}
                >
                  <XIcon className="size-3.5" />
                </span>
              </Badge>
            ))}
          </span>
          {open ? (
            <ChevronUpIcon className="text-primary-700 -mr-1 ml-2 size-5 shrink-0" />
          ) : (
            <ChevronDownIcon className="text-info-800 -mr-1 ml-2 size-5 shrink-0" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('w-(--radix-popover-trigger-width) max-w-none p-0 text-inherit', contentClassName)}
      >
        <div className="flex max-h-96 flex-col">
          {isSearchable && (
            <InputGroup
              className={cn(
                'border-info-300 h-10 w-full shrink-0 overflow-hidden rounded-none border-x-0 border-t-0 border-b shadow-none',
                APP_FOCUS_RING_OVERRIDE,
              )}
            >
              <InputGroupInput placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
            </InputGroup>
          )}
          <div role="listbox" aria-multiselectable className="min-h-0 flex-1 overflow-y-auto p-1">
            {options.length === 0 && <div className="text-muted-foreground p-4 text-center text-sm">No options</div>}
            <Accordion type="multiple" value={accordionValue} onValueChange={setExpandedIds}>
              {renderNodes(options, 0)}
            </Accordion>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
